/**
 * Centralized server-side auth utility.
 *
 * Reads the Stytch session JWT cookie, validates it, and extracts user identity.
 * Replaces the copy-pasted extractEmailFromStytchClaims + ADMIN checks
 * scattered across 8+ API routes.
 *
 * B2B-ready interface: when we migrate to Stytch B2B Organizations,
 * the implementation changes (org comes from JWT claims instead of email domain)
 * but the AuthResult interface stays the same.
 */

import { NextRequest, NextResponse } from "next/server"
import { createRemoteJWKSet, jwtVerify } from "jose"
import { timingSafeEqual } from "crypto"
import { getServiceSupabase } from "@/lib/supabase"
import { isAdminUser, INTERNAL_API_KEY_HEADER } from "./constants"

/**
 * Cached JWKS client for Stytch JWT signature verification.
 * createRemoteJWKSet handles key rotation and caching internally.
 */
const stytchProjectId = process.env.STYTCH_PROJECT_ID || process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN || ""
const JWKS = createRemoteJWKSet(
    new URL(`https://api.stytch.com/v1/sessions/jwks/${stytchProjectId}`)
)

export interface AuthResult {
    /** User's email address */
    email: string
    /** Email domain (e.g. "acme.com") */
    domain: string
    /** Resolved org domain — may differ from email domain for invited users or admin overrides */
    orgDomain: string
    /** Whether the user is a Cardinal admin */
    isAdmin: boolean
    /** Stytch user ID (JWT sub claim) */
    stytchUserId: string
}

export interface AuthOptions {
    /** Resolve the actual org domain via Supabase invited_emails lookup. Default: false */
    resolveOrgDomain?: boolean
    /** Admin override domain (from ?org= query param). Only applied if user is admin. */
    adminOverrideDomain?: string | null
    /** Allow x-internal-key header as alternative auth. Default: false */
    allowInternalKey?: boolean
}

export class AuthError extends Error {
    constructor(
        message: string,
        public statusCode: number = 401
    ) {
        super(message)
        this.name = "AuthError"
    }
}

/**
 * Extract email from Stytch JWT session claims.
 * Supports email magic links, Google OAuth, and Microsoft OAuth factors.
 */
function extractEmailFromStytchClaims(payload: Record<string, unknown>): string | null {
    const sessionClaim = payload["https://stytch.com/session"] as
        | {
              authentication_factors?: Array<{
                  type: string
                  email_factor?: { email_address: string }
                  google_oauth_factor?: { email_address: string }
                  microsoft_oauth_factor?: { email_address: string }
              }>
          }
        | undefined

    if (!sessionClaim?.authentication_factors) return null

    for (const factor of sessionClaim.authentication_factors) {
        if (factor.email_factor?.email_address) return factor.email_factor.email_address
        if (factor.google_oauth_factor?.email_address) return factor.google_oauth_factor.email_address
        if (factor.microsoft_oauth_factor?.email_address) return factor.microsoft_oauth_factor.email_address
    }

    return null
}

/**
 * Resolve the org domain for a user email.
 * Checks if the user is an invited member of a different org via `invited_emails`.
 * Temporary — will be replaced by Stytch B2B org membership in Phase 3.
 */
async function resolveOrgDomainFromSupabase(email: string, emailDomain: string): Promise<string> {
    const supabase = getServiceSupabase()
    const { data: invitedOrg } = await supabase
        .from("organizations")
        .select("domain")
        .contains("invited_emails", [email.toLowerCase()])
        .single()

    return invitedOrg?.domain ?? emailDomain
}

export function safeCompare(a: string, b: string): boolean {
    if (a.length !== b.length) return false
    return timingSafeEqual(Buffer.from(a), Buffer.from(b))
}

/**
 * Authenticate a request by reading the Stytch session JWT cookie.
 * Throws AuthError on failure.
 */
export async function authenticateRequest(
    request: NextRequest,
    options: AuthOptions = {}
): Promise<AuthResult> {
    const { resolveOrgDomain = false, adminOverrideDomain = null, allowInternalKey = false } = options

    // Check internal API key if allowed
    if (allowInternalKey) {
        const internalKey = request.headers.get(INTERNAL_API_KEY_HEADER)
        if (internalKey && process.env.INTERNAL_API_KEY && safeCompare(internalKey, process.env.INTERNAL_API_KEY)) {
            return {
                email: "internal-service@trycardinal.ai",
                domain: "trycardinal.ai",
                orgDomain: "trycardinal.ai",
                isAdmin: true,
                stytchUserId: "internal-service",
            }
        }
    }

    // Verify JWT signature using Stytch's JWKS endpoint.
    // jwtVerify also validates exp automatically.
    const stytchJwt = request.cookies.get("stytch_session_jwt")?.value
    if (!stytchJwt) {
        throw new AuthError("No Stytch session found")
    }

    let payload: Record<string, unknown>
    try {
        const result = await jwtVerify(stytchJwt, JWKS)
        payload = result.payload as Record<string, unknown>
    } catch {
        throw new AuthError("Invalid or expired session")
    }

    // Extract email strictly from verified JWT claims.
    // No query param fallback — that was a privilege escalation vector.
    const stytchUserId = payload.sub as string | undefined
    const email = extractEmailFromStytchClaims(payload)
    if (!email) {
        throw new AuthError("Could not determine user email from session")
    }

    const parts = email.split("@")
    if (parts.length !== 2 || !parts[1]) {
        throw new AuthError("Invalid email format")
    }
    const domain = parts[1].toLowerCase()
    const admin = isAdminUser(email)
    const resolvedUserId = stytchUserId || email

    // Resolve org domain
    let orgDomain = domain
    if (resolveOrgDomain) {
        orgDomain = await resolveOrgDomainFromSupabase(email, domain)
    }

    // Admin override
    if (adminOverrideDomain && admin) {
        orgDomain = adminOverrideDomain.toLowerCase()
    }

    return {
        email,
        domain,
        orgDomain,
        isAdmin: admin,
        stytchUserId: resolvedUserId,
    }
}

/**
 * Authenticate a request, returning a NextResponse on failure instead of throwing.
 * Use in route handlers: `const auth = await authenticateOrError(request); if (auth instanceof NextResponse) return auth;`
 */
export async function authenticateOrError(
    request: NextRequest,
    options: AuthOptions = {}
): Promise<AuthResult | NextResponse> {
    try {
        return await authenticateRequest(request, options)
    } catch (err) {
        if (err instanceof AuthError) {
            return NextResponse.json({ error: err.message }, { status: err.statusCode })
        }
        return NextResponse.json({ error: "Internal server error" }, { status: 500 })
    }
}
